import { Injectable, inject } from '@angular/core';
import {
  collectionChanges,
  collectionData,
  limit,
  orderBy,
  query,
  where,
} from '@angular/fire/firestore';
import { IChatItem, IChatTab, MessagingService } from '@gozetta/z-messaging';
import { combineLatest, defer, from, Observable, of, shareReplay } from 'rxjs';
import {
  distinctUntilChanged,
  map,
  mergeAll,
  startWith,
  switchMap,
} from 'rxjs/operators';
import { CustomersService } from './customers.service';

@Injectable()
export class MessagingClientService extends MessagingService {
  private readonly customerService = inject(CustomersService);

  constructor() {
    super();
  }

  /**
   * Prepares an observable query for retrieving and transforming chat list data
   * for the specified chat tab and array of location IDs.
   * - Splits the provided locations array into chunks of 10 to comply with Firestore's 'in' query limit.
   * - For each chunk, creates a Firestore query to fetch chat items for those locations,
   *   ordered by the 'updatedAt' field in descending order.
   * - Uses `sortedChanges` to listen for real-time updates and maps each change to include
   *   properly formatted date fields using `covertTimestampToDate`.
   * - Combines all chunked observables into a single observable emitting a flat array of chat changes.
   * @param chatList The chat tab configuration object.
   * @param locations An array of location IDs to filter the chat list.
   * @returns An observable emitting a flat array of chat changes, each containing the change type,
   *          the chat item document (with ISO date strings), and index information.
   */
  override getChatListChanges(chatList: IChatTab, locations: number[]) {
    return this.customerService.selectedCustomerIds$.pipe(
      distinctUntilChanged(),
      switchMap(customerIds => {
        const schoolsIds = this.customerService
          .customerSchools()
          .map(school => school.id);

        if (!schoolsIds || schoolsIds.length === 0) {
          return of([]);
        }
        return this._prepareClientChatListQuery(chatList, schoolsIds).pipe(
          map(this.mapChatListChanges.bind(this)),
          map(chatItems =>
            // Return only chat items that belong to the selected customers
            // As the same school can belong to multiple customers
            chatItems.filter(c => customerIds.includes(c.doc.customerId!))
          )
        );
      })
    );
  }

  /**
   * Retrieves and transforms the chat list data for the specified chat tab and array of location IDs.
   * - Splits the provided locations array into chunks of 10 to comply with Firestore's 'in' query limit.
   * - For each chunk, creates a Firestore query to fetch chat items for those locations,
   *   ordered by the 'updatedAt' field in descending order.
   **/
  override _prepareUnreadChat$(): Observable<Record<string, IChatItem[]>> {
    return defer(() =>
      this.customerService.selectedCustomerIds$.pipe(
        distinctUntilChanged(),
        switchMap(customerIds => {
          const schoolsIds = this.customerService
            .customerSchools()
            .map(school => school.id);
          if (!schoolsIds || schoolsIds.length === 0) {
            return of({});
          }
          const _chatTabsQueries$ = this._configs.chatTabs.reduce(
            (acc, chatTab) => {
              return {
                ...acc,
                [chatTab.id]: this._prepareClientUnreadListQuery(
                  chatTab,
                  customerIds,
                  schoolsIds
                ),
              };
            },
            {}
          );
          return combineLatest(_chatTabsQueries$);
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
        startWith({} as Record<string, IChatItem[]>)
      )
    );
  }

  _prepareClientChatListQuery(chatList: IChatTab, schoolsIds: number[]) {
    const collectionRef = this.getChatListCollection(chatList);
    const schoolsChunks = this.chunkArray(schoolsIds, 10);
    const _queries$ = schoolsChunks.map(chunk => {
      const _query = query(
        collectionRef,
        where('schoolId', 'in', chunk),
        orderBy('updatedAt', 'desc'),
        limit(10)
      );
      return collectionChanges(_query);
    });
    return from(_queries$).pipe(mergeAll());
  }

  _prepareClientUnreadListQuery(
    chatList: IChatTab,
    customerIds: number[],
    schoolsIds: number[]
  ): Observable<IChatItem[]> {
    const collectionRef = this.getChatListCollection(chatList);
    const schoolsChunks = this.chunkArray(schoolsIds, 10);
    const _queries$ = schoolsChunks.map(chunk => {
      const _query = query(
        collectionRef,
        where(chatList.conversationUnreadFlag, '!=', 0),
        where('schoolId', 'in', chunk)
      );
      return collectionData(_query).pipe(
        map(
          chatItems =>
            chatItems.map(chatItem => ({
              ...chatItem,
              updatedAt: this.covertTimestampToDate(chatItem['updatedAt']),
              createdAt: this.covertTimestampToDate(chatItem['createdAt']),
              closedAt: this.covertTimestampToDate(chatItem['closedAt']),
              lastMessageDate: this.covertTimestampToDate(
                chatItem['lastMessageDate']
              ),
            })) as IChatItem[]
        ),
        map(chatItems =>
          // Return only chat items that belong to the selected customers
          // As the same school can belong to multiple customers
          chatItems.filter(_c => customerIds.includes(_c.customerId!))
        )
      );
    });
    return combineLatest(_queries$).pipe(
      map(results => this.sortChatList(results.flat()))
    );
  }
}
